import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown, CloudSun, Mountains, Wind } from "@phosphor-icons/react";
import GlobeScene from "./Globe";
import DonateButton from "./DonateButton";
import CarbonCalculator from "./CarbonCalculator";

const floatingChips = [
  {
    icon: CloudSun,
    label: "Weather",
    value: "24.3°C",
    position: "top-[15%] left-[5%] lg:left-[10%]",
    delay: 0.6,
    color: "bg-sky-50 text-sky-700 border-sky-200",
  },
  {
    icon: Mountains,
    label: "Seismic",
    value: "2.1 Mag",
    position: "top-[25%] right-[5%] lg:right-[10%]",
    delay: 0.8,
    color: "bg-orange-50 text-orange-700 border-orange-200",
  },
  {
    icon: Wind,
    label: "Air Quality",
    value: "AQI 42",
    position: "bottom-[25%] left-[3%] lg:left-[8%]",
    delay: 1.0,
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
];

export default function HeroSection() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-stone-50"
    >
      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #78716c 1px, transparent 1px), linear-gradient(to bottom, #78716c 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* Globe - absolute center */}
      <div className="absolute inset-0 flex items-center justify-center px-4">
        <GlobeScene className="w-[min(85vw,380px)] h-[min(85vw,380px)] sm:w-[min(80vw,560px)] sm:h-[min(80vw,560px)] lg:w-[640px] lg:h-[640px]" />
      </div>

      {/* Text overlay */}
      <div className="relative z-10 text-center px-6 pointer-events-none select-none">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-xs tracking-[0.25em] uppercase font-semibold text-emerald-700 mb-4 font-body"
        >
          Real-Time Earth Intelligence
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter font-light text-stone-900 leading-[0.95]"
        >
          Monitor Our
          <br />
          <span className="font-medium text-emerald-700">Living Planet</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-6 text-sm sm:text-base lg:text-lg text-stone-500 max-w-md mx-auto font-body leading-relaxed px-2"
        >
          Weather, seismic activity, and air quality — unified in one
          interactive platform.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-8 flex flex-wrap items-center justify-center gap-3 sm:gap-4 pointer-events-auto"
        >
          <a
            href="#features"
            data-testid="hero-cta-explore"
            className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium px-8 py-3 rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-xl shadow-emerald-700/20"
          >
            Start Exploring
          </a>
          <Link
            to="/dashboard"
            data-testid="hero-cta-dashboard"
            className="border border-stone-300 hover:border-emerald-600 hover:bg-emerald-50 text-stone-600 hover:text-emerald-700 text-sm font-medium px-8 py-3 rounded-full transition-all duration-300 hover:-translate-y-1"
          >
            Live Dashboard
          </Link>
          <DonateButton variant="primary" label="Donate" />
          <CarbonCalculator />
        </motion.div>
      </div>

      {/* Floating metric chips */}
      {floatingChips.map((chip) => (
        <motion.div
          key={chip.label}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: chip.delay }}
          className={`absolute ${chip.position} hidden sm:flex items-center gap-2.5 px-4 py-2.5 rounded-2xl border backdrop-blur-md shadow-lg ${chip.color} z-20`}
          data-testid={`floating-chip-${chip.label.toLowerCase().replace(" ", "-")}`}
        >
          <chip.icon size={18} weight="bold" />
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-wider opacity-70 font-body">
              {chip.label}
            </p>
            <p className="text-sm font-semibold font-heading">{chip.value}</p>
          </div>
        </motion.div>
      ))}

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <a
          href="#features"
          data-testid="scroll-indicator"
          className="flex flex-col items-center text-stone-400 hover:text-stone-600 transition-colors"
        >
          <span className="text-[10px] uppercase tracking-[0.2em] font-body mb-2">
            Scroll
          </span>
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ArrowDown size={16} />
          </motion.div>
        </a>
      </motion.div>
    </section>
  );
}
